import {
  AlarmClock,
  Archive,
  BookOpen,
  BriefcaseBusiness,
  CalendarDays,
  Car,
  CircleDot,
  Coffee,
  Dumbbell,
  FileText,
  HeartPulse,
  House,
  Laptop,
  Lightbulb,
  Mail,
  NotebookPen,
  PackageCheck,
  Phone,
  Plane,
  ReceiptText,
  ShoppingCart,
  Stethoscope,
  Tags,
  Truck,
  Users,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useState } from "react";

export type IconOption = { key: string; label: string; Icon: LucideIcon };

export const ENTRY_ICON_OPTIONS: IconOption[] = [
  { key: "alarm-clock", label: "Alarm Clock", Icon: AlarmClock },
  { key: "file-text", label: "Notiz", Icon: FileText },
  { key: "notebook-pen", label: "Idee", Icon: NotebookPen },
  { key: "circle-dot", label: "Allgemein", Icon: CircleDot },
  { key: "lightbulb", label: "Idee", Icon: Lightbulb },
  { key: "phone", label: "Telefon", Icon: Phone },
  { key: "mail", label: "E-Mail", Icon: Mail },
  { key: "receipt-text", label: "Rechnung", Icon: ReceiptText },
  { key: "package-check", label: "Paket", Icon: PackageCheck },
  { key: "truck", label: "Lieferung", Icon: Truck },
  { key: "stethoscope", label: "Gesundheit", Icon: Stethoscope },
  { key: "heart-pulse", label: "Gesundheit", Icon: HeartPulse },
  { key: "calendar-days", label: "Termin", Icon: CalendarDays },
  { key: "shopping-cart", label: "Einkauf", Icon: ShoppingCart },
  { key: "briefcase-business", label: "Arbeit", Icon: BriefcaseBusiness },
  { key: "users", label: "Menschen", Icon: Users },
  { key: "house", label: "Zuhause", Icon: House },
  { key: "car", label: "Auto", Icon: Car },
  { key: "plane", label: "Reise", Icon: Plane },
  { key: "dumbbell", label: "Sport", Icon: Dumbbell },
  { key: "coffee", label: "Pause", Icon: Coffee },
  { key: "laptop", label: "Digital", Icon: Laptop },
  { key: "book-open", label: "Lesen", Icon: BookOpen },
  { key: "tags", label: "Organisation", Icon: Tags },
  { key: "archive", label: "Ablage", Icon: Archive },
  { key: "wrench", label: "Reparatur", Icon: Wrench },
];

const fallbackIcons = ["file-text", "notebook-pen", "circle-dot", "lightbulb", "tags"];
const semanticIcons: Array<{ terms: string[]; icon: string }> = [
  { terms: ["anruf", "rückruf", "telefon", "call"], icon: "phone" },
  { terms: ["rechnung", "beleg", "steuer", "zahlung"], icon: "receipt-text" },
  { terms: ["retoure", "paket", "liefer", "versand", "waren"], icon: "package-check" },
  { terms: ["arzt", "medizin", "medikament", "praxis"], icon: "stethoscope" },
  { terms: ["mail", "e-mail", "brief", "nachricht"], icon: "mail" },
  { terms: ["termin", "kalender", "meeting"], icon: "calendar-days" },
  { terms: ["einkauf", "bestellen", "supermarkt"], icon: "shopping-cart" },
  { terms: ["reise", "flug", "urlaub", "hotel"], icon: "plane" },
  { terms: ["auto", "werkstatt", "fahren"], icon: "car" },
  { terms: ["arbeit", "projekt", "kunde", "büro"], icon: "briefcase-business" },
  { terms: ["haus", "wohnung", "familie"], icon: "house" },
  { terms: ["sport", "training", "fitness"], icon: "dumbbell" },
];

const byKey = new Map(ENTRY_ICON_OPTIONS.map((option) => [option.key, option]));

function stableIndex(value: string, length: number) {
  return [...value].reduce((hash, character) => (hash * 31 + character.charCodeAt(0)) >>> 0, 7) % length;
}

export function suggestEntryIcon(title: string, tags: string[] = []) {
  const value = `${title} ${tags.join(" ")}`.toLocaleLowerCase("de-DE");
  const match = semanticIcons.find(({ terms }) => terms.some((term) => value.includes(term)));
  return match?.icon || fallbackIcons[stableIndex(value || "memo", fallbackIcons.length)];
}

export function EntryIcon({ iconKey, title, size = 17 }: { iconKey?: string | null; title: string; size?: number }) {
  const requestedKey = iconKey || suggestEntryIcon(title);
  const fallbackOption = byKey.get(suggestEntryIcon(title)) || ENTRY_ICON_OPTIONS[0];
  const [catalogOption, setCatalogOption] = useState<IconOption | null>(null);
  const staticOption = byKey.get(requestedKey);
  useEffect(() => {
    if (staticOption) {
      setCatalogOption(null);
      return;
    }
    let active = true;
    void import("./iconCatalog").then(({ findEntryIconOption }) => {
      if (active) setCatalogOption(findEntryIconOption(requestedKey) || null);
    });
    return () => { active = false; };
  }, [requestedKey, staticOption]);
  const option = staticOption || catalogOption || fallbackOption;
  const Icon = option.Icon;
  return <Icon size={size} strokeWidth={1.8} aria-hidden="true" />;
}

export function IconPicker({ value, title, tags, onChange }: { value?: string | null; title: string; tags: string[]; onChange: (iconKey: string) => void }) {
  const [query, setQuery] = useState(""), [availableOptions, setAvailableOptions] = useState(ENTRY_ICON_OPTIONS);
  useEffect(() => {
    let active = true;
    void import("./iconCatalog").then(({ allEntryIconOptions }) => {
      if (active) setAvailableOptions(allEntryIconOptions);
    });
    return () => { active = false; };
  }, []);
  const normalized = query.trim().toLocaleLowerCase("de-DE");
  const options = availableOptions.filter((option) => !normalized || `${option.key} ${option.label}`.toLocaleLowerCase("de-DE").includes(normalized));
  const suggested = suggestEntryIcon(title, tags);
  return (
    <div className="icon-picker" role="group" aria-label="Symbol auswählen">
      <input aria-label="Symbole durchsuchen" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Symbol suchen" />
      <button type="button" className={!value ? "active" : ""} aria-pressed={!value} title="Automatisch passendes Symbol verwenden" onClick={() => onChange("")}>
        <EntryIcon iconKey={suggested} title={title} />
        <span>Automatisch</span>
      </button>
      <div className="icon-picker-grid">
        {options.map(({ key, label, Icon }) => (
          <button type="button" key={key} className={value === key ? "active" : ""} aria-label={`Symbol ${label}`} aria-pressed={value === key} title={label} onClick={() => onChange(key)}>
            <Icon size={17} strokeWidth={1.8} aria-hidden="true" />
          </button>
        ))}
      </div>
    </div>
  );
}
